/**
 * DPDP Consent Engine — API Client
 * All calls to the backend Consent Engine API.
 * Source of truth is always the backend — no client-side-only consent storage.
 */

import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const DPDP_API = `${BASE_URL}/api/v1/dpdp`;

// ---------------------------------------------------------------------------
// Axios instance with consent-specific headers
// ---------------------------------------------------------------------------

const dpdpClient = axios.create({
  baseURL: DPDP_API,
  headers: {
    "Content-Type": "application/json",
  },
  // Attach HttpOnly cookies (for backend-set consent cookies)
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Notices
// ---------------------------------------------------------------------------

/**
 * Fetch the active consent notice for a tenant.
 * Used by ConsentBanner to display notice text in the user's language.
 */
export const getActiveNotice = async (language = "en") => {
  const res = await dpdpClient.get("/notices/active", {
    params: { language },
  });
  return res.data;
};

/**
 * List all notices for a tenant (paginated).
 */
export const listNotices = async (skip = 0, limit = 20) => {
  const res = await dpdpClient.get("/notices", { params: { skip, limit } });
  return res.data;
};

// ---------------------------------------------------------------------------
// Consent Lifecycle
// ---------------------------------------------------------------------------

/**
 * Grant consent for selected purposes.
 * Requires a valid JWT Bearer token (attached by the interceptor).
 */
export const grantConsent = async (payload) => {
  const res = await dpdpClient.post("/consent/grant", payload);
  return res.data;
};

/**
 * Withdraw consent for selected purposes (or all).
 * Requires a valid JWT Bearer token.
 */
export const withdrawConsent = async (payload) => {
  const res = await dpdpClient.post("/consent/withdraw", payload);
  return res.data;
};

/**
 * Get current consent status for the authenticated user.
 * For anonymous users (no JWT), pass data_principal_id explicitly.
 * Returns hashed identifiers only — never raw PII.
 */
export const getConsentStatus = async (dataPrincipalId = null) => {
  const params = {};
  if (dataPrincipalId) params.data_principal_id = dataPrincipalId;
  const res = await dpdpClient.get("/consent/status", { params });
  return res.data;
};

/**
 * Get full consent event history for the authenticated user.
 * For anonymous users (no JWT), pass data_principal_id explicitly.
 */
export const getConsentHistory = async (dataPrincipalId = null) => {
  const params = {};
  if (dataPrincipalId) params.data_principal_id = dataPrincipalId;
  const res = await dpdpClient.get("/consent/history", { params });
  return res.data;
};

// ---------------------------------------------------------------------------
// Data Principal Rights
// ---------------------------------------------------------------------------

/**
 * File a new Data Principal Rights request.
 * Valid request_types: access, erasure, portability, correction, nomination, grievance
 */
export const fileRightsRequest = async (payload) => {
  const res = await dpdpClient.post("/rights/request", payload);
  return res.data;
};

/**
 * Get a specific DPR request by ID.
 */
export const getRightsRequest = async (requestId) => {
  const res = await dpdpClient.get(`/rights/request/${requestId}`);
  return res.data;
};

/**
 * List all DPR requests for the authenticated user's tenant (DPO view).
 */
export const listRightsRequests = async (statusFilter = null, skip = 0, limit = 20) => {
  const params = { skip, limit };
  if (statusFilter) params.status_filter = statusFilter;
  const res = await dpdpClient.get("/rights/requests", { params });
  return res.data;
};

// ---------------------------------------------------------------------------
// Parental Consent (DPDP Section 10)
// ---------------------------------------------------------------------------

function _getUserIdentifier() {
  try {
    const user = JSON.parse(localStorage.getItem("rdw_user") ?? "null");
    return user?.id || user?.sub || user?.email || `anon_${Date.now()}`;
  } catch {
    return `anon_${Date.now()}`;
  }
}

/**
 * Initiate parental consent request (minor action).
 * POST /api/v1/dpdp/consent/minor/initiate
 */
export const initiateMinorConsent = async (payload) => {
  const res = await dpdpClient.post("/consent/minor/initiate", {
    minor_data_principal_id: _getUserIdentifier(),
    parent_contact_method: payload.parent_contact_method,
    parent_contact_value: payload.parent_contact_value,
  });
  return res.data;
};

/**
 * Check status of parental consent request (minor polls this).
 * GET /api/v1/dpdp/consent/minor/status?verification_id=xxx
 */
export const getMinorConsentStatus = async (verificationId) => {
  const res = await dpdpClient.get("/consent/minor/status", {
    params: { verification_id: verificationId },
  });
  return res.data;
};

/**
 * Parent verifies consent for minor.
 * POST /api/v1/dpdp/consent/minor/verify
 */
export const verifyParentConsent = async (payload) => {
  const res = await dpdpClient.post("/consent/minor/verify", {
    token: payload.token,
    parent_data_principal_id: payload.parent_data_principal_id || null,
    verification_method: payload.verification_method,
    digilocker_auth_code: payload.digilocker_auth_code || null,
  });
  return res.data;
};

/**
 * List children linked to this parent account.
 * GET /api/v1/dpdp/consent/parent/children
 */
export const getParentChildren = async () => {
  const res = await dpdpClient.get("/consent/parent/children");
  return res.data;
};

/**
 * Revoke parental consent for a specific child.
 * DELETE /api/v1/dpdp/consent/parent/children/{verification_id}
 */
export const revokeChildConsent = async (verificationId) => {
  const res = await dpdpClient.delete(`/consent/parent/children/${verificationId}`);
  return res.data;
};

// ---------------------------------------------------------------------------
// Breach Notification (DPDP Section 8(6))
// ---------------------------------------------------------------------------

/**
 * Create a breach record via DPO intake form.
 * POST /api/v1/dpdp/breach
 */
export const reportBreach = async (payload) => {
  const res = await dpdpClient.post("/breach", payload);
  return res.data;
};

/**
 * Get a specific breach record.
 * GET /api/v1/dpdp/breach/{breach_id}
 */
export const getBreachDetails = async (breachId) => {
  const res = await dpdpClient.get(`/breach/${breachId}`);
  return res.data;
};

/**
 * List all breaches for the tenant (DPO view).
 * GET /api/v1/dpdp/breach
 */
export const listBreaches = async (params = {}) => {
  const res = await dpdpClient.get("/breach", { params });
  return res.data;
};

/**
 * List overdue breaches (DPB deadline missed).
 * GET /api/v1/dpdp/breach/overdue
 */
export const listOverdueBreaches = async () => {
  const res = await dpdpClient.get("/breach/overdue");
  return res.data;
};

/**
 * Update breach status (contain, notify, resolve, close).
 * PATCH /api/v1/dpdp/breach/{breach_id}
 */
export const updateBreachStatus = async (breachId, payload) => {
  const res = await dpdpClient.patch(`/breach/${breachId}`, payload);
  return res.data;
};

/**
 * Acknowledge a breach and begin investigation.
 * POST /api/v1/dpdp/breach/{breach_id}/acknowledge
 */
export const acknowledgeBreach = async (breachId, payload = {}) => {
  const res = await dpdpClient.post(`/breach/${breachId}/acknowledge`, payload);
  return res.data;
};

/**
 * Run a breach simulation drill (dev/staging only).
 * POST /api/v1/dpdp/breach/drill
 */
export const runBreachDrill = async (payload) => {
  const res = await dpdpClient.post("/breach/drill", payload);
  return res.data;
};

/**
 * List past breach simulation drills.
 * GET /api/v1/dpdp/breach/drill
 */
export const listBreachDrills = async (params = {}) => {
  const res = await dpdpClient.get("/breach/drill", { params });
  return res.data;
};

// ---------------------------------------------------------------------------
// Audit & Compliance (DPDP)
// ---------------------------------------------------------------------------

/**
 * Verify audit chain integrity.
 * POST /api/v1/dpdp/admin/audit/verify
 */
export const verifyAuditChain = async (payload) => {
  const res = await dpdpClient.post("/admin/audit/verify", payload);
  return res.data;
};

/**
 * Export audit log for DPB submission.
 * GET /api/v1/dpdp/admin/audit/export
 */
export const exportAuditLog = async (params) => {
  const res = await dpdpClient.get("/admin/audit/export", { params });
  return res.data;
};

/**
 * Get compliance scorecard (all 5 score cards).
 * GET /api/v1/dpdp/admin/compliance/scorecard
 */
export const getComplianceScorecard = async () => {
  const res = await dpdpClient.get("/admin/compliance/scorecard");
  return res.data;
};

/**
 * Get DPO action center items.
 * GET /api/v1/dpdp/admin/compliance/actions
 */
export const getDPOActionCenter = async () => {
  const res = await dpdpClient.get("/admin/compliance/actions");
  return res.data;
};

/**
 * Register a data collection point.
 * POST /api/v1/dpdp/admin/compliance/data-collection-points
 */
export const registerDataCollectionPoint = async (payload) => {
  const res = await dpdpClient.post("/admin/compliance/data-collection-points", payload);
  return res.data;
};

/**
 * List data collection points.
 * GET /api/v1/dpdp/admin/compliance/data-collection-points
 */
export const listDataCollectionPoints = async (params = {}) => {
  const res = await dpdpClient.get("/admin/compliance/data-collection-points", { params });
  return res.data;
};

/**
 * Generate a monthly compliance report.
 * POST /api/v1/dpdp/admin/compliance/reports/generate
 */
export const generateMonthlyReport = async (year, month) => {
  const res = await dpdpClient.post("/admin/compliance/reports/generate", null, {
    params: { year, month },
  });
  return res.data;
};

/**
 * List compliance reports.
 * GET /api/v1/dpdp/admin/compliance/reports
 */
export const listComplianceReports = async (params = {}) => {
  const res = await dpdpClient.get("/admin/compliance/reports", { params });
  return res.data;
};

/**
 * Get a specific compliance report (includes PDF HTML).
 * GET /api/v1/dpdp/admin/compliance/reports/{report_id}
 */
export const getComplianceReport = async (reportId) => {
  const res = await dpdpClient.get(`/admin/compliance/reports/${reportId}`);
  return res.data;
};

/**
 * Digitally sign a compliance report.
 * POST /api/v1/dpdp/admin/compliance/reports/{report_id}/sign?dpo_name=...
 */
export const signComplianceReport = async (reportId, dpoName) => {
  const res = await dpdpClient.post(
    `/admin/compliance/reports/${reportId}/sign`,
    null,
    { params: { dpo_name: dpoName } }
  );
  return res.data;
};

// ---------------------------------------------------------------------------
// Vendor DPA Management (Safeguard 6)
// ---------------------------------------------------------------------------

/**
 * Register a new vendor in the DPA registry.
 * Risk score is auto-calculated from data categories.
 */
export const registerVendor = async (payload) => {
  const res = await dpdpClient.post("/admin/vendors", payload);
  return res.data;
};

/**
 * List all vendors with optional filtering.
 */
export const listVendors = async (params = {}) => {
  const res = await dpdpClient.get("/admin/vendors", { params });
  return res.data;
};

/**
 * Get a single vendor by ID, including live DPA status.
 */
export const getVendor = async (vendorId) => {
  const res = await dpdpClient.get(`/admin/vendors/${vendorId}`);
  return res.data;
};

/**
 * Update a vendor's details.
 */
export const updateVendor = async (vendorId, payload) => {
  const res = await dpdpClient.put(`/admin/vendors/${vendorId}`, payload);
  return res.data;
};

/**
 * Soft-delete a vendor (mark inactive).
 */
export const deleteVendor = async (vendorId) => {
  await dpdpClient.delete(`/admin/vendors/${vendorId}`);
};

/**
 * List DPA documents for a vendor.
 */
export const listVendorDPADocuments = async (vendorId) => {
  const res = await dpdpClient.get(`/admin/vendors/${vendorId}/dpa`);
  return res.data;
};

/**
 * Upload a DPA document for a vendor.
 */
export const uploadVendorDPA = async (vendorId, dpoName, signedDate) => {
  const res = await dpdpClient.post(
    `/admin/vendors/${vendorId}/dpa`,
    {},
    { params: { dpo_name: dpoName, signed_date: signedDate } }
  );
  return res.data;
};

/**
 * Get vendor risk distribution summary.
 */
export const getVendorRiskSummary = async () => {
  const res = await dpdpClient.get("/admin/vendors/risk-summary");
  return res.data;
};

/**
 * Get vendors requiring DPO attention (expiring, expired, missing DPA).
 */
export const getVendorAlerts = async () => {
  const res = await dpdpClient.get("/admin/vendors/alerts");
  return res.data;
};

/**
 * Generate a DPDP-compliant DPA document as HTML (printable to PDF).
 */
export const generateDPATemplate = async (payload) => {
  const res = await dpdpClient.post("/admin/vendors/dpa/generate-template", payload);
  return res.data;
};

/**
 * Read the JWT token from the rdw_user localStorage entry and attach to
 * all DPDP API calls via Authorization header.
 */
dpdpClient.interceptors.request.use((config) => {
  try {
    const rdwUser = localStorage.getItem("rdw_user");
    if (rdwUser) {
      const user = JSON.parse(rdwUser);
      if (user?.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    }
    // Also check for rdw_jwt (Google OAuth flow)
    const jwtToken = localStorage.getItem("rdw_jwt");
    if (jwtToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${jwtToken}`;
    }
  } catch {
    // localStorage not available or parse error — proceed without auth
  }
  return config;
});

// ---------------------------------------------------------------------------
// Error normalisation
// ---------------------------------------------------------------------------

dpdpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const serverError = error.response?.data?.error;
    if (serverError) {
      return Promise.reject(new Error(`${serverError.code}: ${serverError.message}`));
    }
    return Promise.reject(error);
  }
);

export default {
  getActiveNotice,
  listNotices,
  grantConsent,
  withdrawConsent,
  getConsentStatus,
  getConsentHistory,
  fileRightsRequest,
  getRightsRequest,
  listRightsRequests,
  initiateMinorConsent,
  getMinorConsentStatus,
  verifyParentConsent,
  getParentChildren,
  revokeChildConsent,
  reportBreach,
  getBreachDetails,
  listBreaches,
  listOverdueBreaches,
  updateBreachStatus,
  acknowledgeBreach,
  runBreachDrill,
  listBreachDrills,
  verifyAuditChain,
  exportAuditLog,
  getComplianceScorecard,
  getDPOActionCenter,
  registerDataCollectionPoint,
  listDataCollectionPoints,
  generateMonthlyReport,
  listComplianceReports,
  getComplianceReport,
  signComplianceReport,
  // Vendor DPA Management (Safeguard 6)
  registerVendor,
  listVendors,
  getVendor,
  updateVendor,
  deleteVendor,
  listVendorDPADocuments,
  uploadVendorDPA,
  getVendorRiskSummary,
  getVendorAlerts,
  generateDPATemplate,
};
